import { useContext } from "react";
import ErrorBanner from "components/Common/ErrorBanner/ErrorBanner.tsx";
import ConfigurationsContext from "context/configurations/ConfigurationsContext.ts";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import { UserChannel } from "types/declarations/enums.ts";
import { getSupportFormUrl } from "utils/supportForm.ts";
import * as PageStyles from "./SupportPage.styles.ts";
import * as Styles from "./SupportRequestPage.styles.ts";
import { useNavigate } from "react-router";

interface SupportRequestOption {
	id: string;
	image: string;
	imageWidth?: number;
	label: string;
	href?: string;
	to?: string;
}

const standardOptions: SupportRequestOption[] = [
	{
		id: "question",
		image: "/assets/support/question.svg",
		imageWidth: 80,
		label: "I have questions.",
		to: "/support/request/questions"
	},
	{
		id: "order-help",
		image: "/assets/support/order_help.svg",
		imageWidth: 130,
		label: "I need help with an order.",
		to: "/support/request/order-help"
	},
	{
		id: "problem",
		image: "/assets/support/problem.svg",
		imageWidth: 69,
		label: "I have a problem.",
		to: "/support/request/problem"
	},
	{ id: "suggestion", image: "/assets/support/suggestion.svg", label: "I have a suggestion." }
];

const returnAuthorizationOption: SupportRequestOption = {
	id: "return-authorization",
	image: "/assets/support/return_auth.svg",
	imageWidth: 124,
	label: "I need a return authorization.",
	href: getSupportFormUrl({ type: "Return Authorization" })
};

const designSupportOption: SupportRequestOption = {
	id: "design-support",
	image: "/assets/support/design_support.svg",
	imageWidth: 100,
	label: "I need design support."
};

const SupportOptionContent = ({ image, imageWidth, label }: SupportRequestOption) => (
	<>
		<Styles.OptionImage
			alt=""
			aria-hidden="true"
			imageWidth={imageWidth}
			src={image}
		/>
		<Styles.OptionLabel>{label}</Styles.OptionLabel>
	</>
);

const SupportOption = (option: SupportRequestOption) => {
	const { id, href, label, to } = option;
	const navigate = useNavigate();

	if (href) {
		return (
			<Styles.OptionLink
				aria-label={label}
				data-testid={`support-request-${id}`}
				href={href}
				rel="noreferrer"
				target="_blank"
			>
				<SupportOptionContent {...option} />
			</Styles.OptionLink>
		);
	}

	return (
		<Styles.OptionButton
			data-testid={`support-request-${id}`}
			onClick={to ? () => navigate(to) : undefined}
			type="button"
		>
			<SupportOptionContent {...option} />
		</Styles.OptionButton>
	);
};

const SupportRequestPage = () => {
	const { data: userDetails, isError, isLoading } = useGetUserDetailsQuery();
	const { configurationsData } = useContext(ConfigurationsContext);
	const userChannel = userDetails?.userChannel;
	const isHomeDepot = userChannel === UserChannel.HOME_DEPOT;
	const canRequestReturnAuthorization =
		configurationsData?.featureFlags?.returnAuthEnabled === true &&
		(isHomeDepot || userChannel === UserChannel.LOWES);
	const conditionalOptions = [
		...(canRequestReturnAuthorization ? [returnAuthorizationOption] : []),
		...(isHomeDepot ? [designSupportOption] : [])
	];

	return (
		<PageStyles.Page data-testid="support-request-page">
			<PageStyles.Header>
				<PageStyles.Title>Support Request</PageStyles.Title>
			</PageStyles.Header>

			<Styles.Content aria-labelledby="support-request-prompt">
				<Styles.Prompt id="support-request-prompt">How can we help you today?</Styles.Prompt>

				{isLoading && (
					<Styles.StandardOptions data-testid="support-request-loading">
						{standardOptions.map((option) => (
							<Styles.OptionSkeleton
								key={option.id}
								variant="circular"
							/>
						))}
					</Styles.StandardOptions>
				)}

				{isError && (
					<ErrorBanner errorMessaging="Unable to load support request options. Please try again later." />
				)}

				{!isLoading && !isError && (
					<>
						<Styles.StandardOptions>
							{standardOptions.map((option) => (
								<SupportOption
									key={option.id}
									{...option}
								/>
							))}
						</Styles.StandardOptions>

						{conditionalOptions.length > 0 && (
							<Styles.ConditionalOptions>
								{conditionalOptions.map((option) => (
									<SupportOption
										key={option.id}
										{...option}
									/>
								))}
							</Styles.ConditionalOptions>
						)}
					</>
				)}
			</Styles.Content>
		</PageStyles.Page>
	);
};

export default SupportRequestPage;
